import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authClient } from 'src/services/auth/client';
import { clearAccessToken } from 'src/services/auth/tokenStore';
import { useAppStore } from 'src/states/app';

type AuthError = { message: string } | null;

export function useAuthActions() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<AuthError>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  async function run<T>(action: () => Promise<{ data: T | null; error: { message?: string } | null }>) {
    setIsPending(true);
    setError(null);
    try {
      const result = await action();
      if (result.error) {
        setError({ message: result.error.message ?? 'Unknown error' });
        return null;
      }
      return result.data;
    } finally {
      setIsPending(false);
    }
  }

  return {
    isPending,
    error,
    signIn: (email: string, password: string) => run(() => authClient.signIn.email({ email, password })),
    signUp: (email: string, password: string, name: string) =>
      // The locale the user is actually reading the portal in decides which language
      // their verification and welcome mail arrive in.
      run(() => authClient.signUp.email({ email, password, name, locale: useAppStore.getState().locale })),
    signInSocial: (provider: 'google' | 'facebook') =>
      authClient.signIn.social({ provider, callbackURL: window.location.origin }),
    requestPasswordReset: (email: string) =>
      run(() => authClient.requestPasswordReset({ email, redirectTo: `${window.location.origin}/reset-password` })),
    resetPassword: (newPassword: string, token: string) => run(() => authClient.resetPassword({ newPassword, token })),
    resendVerification: (email: string) =>
      run(() => authClient.sendVerificationEmail({ email, callbackURL: `${window.location.origin}/` })),
    signOut: async () => {
      await authClient.signOut();
      clearAccessToken();
      queryClient.clear();
      navigate('/sign-in', { replace: true });
    },
  };
}
